// This is a modified version of
// https://github.com/aws-samples/amazon-kinesis-video-streams-demos/blob/master/webrtc-js/src/MasterView.tsx
// Commit 880f554
// Original license Apache Version 2.0
//
// Modifications include:
// - Add JoinStorageSessionCommand code
// - remove typescript
// - remove view and other unused code
import React from 'react';

import {
  DescribeSignalingChannelCommand,
  GetSignalingChannelEndpointCommand,
  KinesisVideoClient,
} from '@aws-sdk/client-kinesis-video';
import {
  GetIceServerConfigCommand,
  KinesisVideoSignalingClient,
} from '@aws-sdk/client-kinesis-video-signaling';
import {
  Role,
  SignalingClient
} from 'amazon-kinesis-video-streams-webrtc';
import {
  KinesisVideoWebRTCStorageClient,
  JoinStorageSessionCommand,
} from '@aws-sdk/client-kinesis-video-webrtc-storage';

const liveMaster = {};
const storageMaster = {};

const uid = () => (
  Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
);

const RESOLUTION = { width: {ideal: 640}, height: {ideal: 480} };

class StoreStream extends React.Component {
  constructor(props) {
    super(props);
  }

  componentWillUnmount() {
    const master = this.props.storage ? storageMaster : liveMaster;

    console.debug('[VIEWER] Stopping viewer connection');
    if (master.signalingClient) {
      master.signalingClient.close();
      master.signalingClient = null;
    }

    if (master.peerConnection) {
      master.peerConnection.close();
      master.peerConnection = null;
    }

    if (master.localStream) {
      master.localStream.getTracks().forEach(track => track.stop());
      master.localStream = null;
    }

    if (master.remoteView) {
      master.remoteView.srcObject = null;
    }
  }

  async componentDidMount() {
    const master = this.props.storage ? storageMaster : liveMaster;

    try {
      // Create KVS client
      const kinesisVideoClient = new KinesisVideoClient({
        region: this.props.region,
        credentials: this.props.credentials,
      });

      // Get signaling channel ARN
      const channelARN = this.props.storage ? this.props.storageChannelARN :
        this.props.channelARN;
      console.log('[MASTER] Channel ARN:', channelARN);

      // Get endpoints needed to connect to Amazon Kinesis Video Signaling.
      const getSignalingChannelEndpointResponse = await kinesisVideoClient.send(
        new GetSignalingChannelEndpointCommand({
          ChannelARN: channelARN,
          SingleMasterChannelEndpointConfiguration: {
            Protocols: (
              this.props.storage ? ['WSS', 'HTTPS', 'WEBRTC'] : ['WSS', 'HTTPS']
            ),
            Role: 'MASTER',
          },
        })
      );

      const endpointsByProtocol = getSignalingChannelEndpointResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
        endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
        return endpoints;
      }, {});
      console.debug('[MASTER] Endpoints:', endpointsByProtocol);

      // Create KVS signaling client - HTTPS endpoint (above) is needed here
      const kinesisVideoSignalingClient = new KinesisVideoSignalingClient({
        region: this.props.region,
        credentials: this.props.credentials,
        endpoint: endpointsByProtocol.HTTPS,
      });

      const iceServers = [];

      // Add STUN server
      iceServers.push({
        urls: `stun:stun.kinesisvideo.${this.props.region}.amazonaws.com:443`
      });

      // Add TURN servers
      // Get ICE server configuration
      const getIceServerConfigResponse = await kinesisVideoSignalingClient.send(
        new GetIceServerConfigCommand({
          ChannelARN: channelARN,
        })
      );

      getIceServerConfigResponse.IceServerList.forEach(iceServer =>
        iceServers.push({
          urls: iceServer.Uris,
          username: iceServer.Username,
          credential: iceServer.Password,
        }),
      );
      console.debug('[MASTER] ICE servers:', iceServers);

      // Create Kinesis Video Signaling Client - As master
      master.signalingClient = new SignalingClient({
        channelARN,
        channelEndpoint: endpointsByProtocol.WSS,
        role: Role.MASTER,
        region: this.props.region,
        credentials: {
          accessKeyId: this.props.credentials.accessKeyId,
          secretAccessKey: this.props.credentials.secretAccessKey,
          sessionToken: this.props.credentials.sessionToken,
        },
      });

      const constraints = { video: RESOLUTION, audio: true };

      master.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      master.signalingClient.on('open', async () => {
        console.debug('[MASTER] Connected to signaling service. Waiting for viewers...');

        // STORAGE CLIENT SETUP
        if (this.props.storage) {
          const storageClient = new KinesisVideoWebRTCStorageClient({
            endpoint: endpointsByProtocol.WEBRTC,
            region: this.props.region,
            credentials: this.props.credentials,
          });

          const command = new JoinStorageSessionCommand({
            channelArn: channelARN
          });
          const response = await storageClient.send(command);
          console.debug('[MASTER] Join Storage Session', response);
          // END STORAGE CLIENT SETUP
        }
      });

      master.signalingClient.on('sdpOffer', async (offer, remoteClientId) => {
        if (master.peerConnection) {
          master.peerConnection.close();
        }

        // Configure the RTCPeerConnection. A list of options can be found here:
        // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/RTCPeerConnection#parameters
        const configuration = {
          iceServers,
          iceTransportPolicy: 'all',
        };

        master.peerConnection = new RTCPeerConnection(configuration);
        const peerConnection = master.peerConnection;

        peerConnection.addEventListener('connectionstatechange', async () => {
          console.debug('[MASTER] Connection state changed:', peerConnection.connectionState)
        });

        // Send any ICE candidates to the other peer (remote/viewer)
        peerConnection.addEventListener('icecandidate', ({candidate}) => {
          if (candidate) {
            console.debug('[MASTER] Generated ICE candidate', candidate, 'for client', remoteClientId);
            master.signalingClient.sendIceCandidate(candidate, remoteClientId);
          } else {
            console.debug('[MASTER] All ICE candidates have been generated for client', remoteClientId);
          }
        });

        // If there's no video/audio, master.localStream will be null. So, we should skip adding the tracks from it.
        if (master.localStream) {
          master.localStream.getTracks().forEach(track => (
            peerConnection.addTrack(track, master.localStream)
          ));
        }
        await peerConnection.setRemoteDescription(offer);

        // Create an SDP answer to send back to the client
        console.debug('[MASTER] Creating SDP answer for client', remoteClientId);
        await peerConnection.setLocalDescription(
          await peerConnection.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
          }),
        );

        console.debug('[MASTER] 216 Sending SDP answer to client', remoteClientId);
        const correlationId = uid();
        console.debug('[MASTER] 218 SDP answer:', peerConnection.localDescription, remoteClientId, 'correlationId:', correlationId);
        master.signalingClient.sendSdpAnswer(peerConnection.localDescription, remoteClientId, correlationId);
        console.debug('[MASTER] Generating ICE candidates for client', remoteClientId);
      });

      master.signalingClient.on('iceCandidate', async (candidate, remoteClientId) => {
        console.debug('[MASTER] Received ICE candidate from remote peer (viewer)', remoteClientId, candidate);

        // Add the ICE candidate received from the client to the peer connection
        const peerConnection = master.peerConnection;
        peerConnection.addIceCandidate(candidate);
      });

      master.signalingClient.on('statusResponse', (statusResponse) => {
        if (statusResponse.success) {
          return;
        }
        console.error('[MASTER] Received response from Signaling:', statusResponse);
      });

      master.signalingClient.on('close', () => {
          console.debug('[MASTER] Disconnected from signaling channel');
      });

      master.signalingClient.on('error', error => {
        console.error('[MASTER] Signaling client error', error);
      });

      console.debug('[MASTER] Starting master connection');
      master.signalingClient.open();
    } catch (e) {
      //alert('An error occurred. Check the debug console.')
      console.error('[MASTER]', this.props, e);
    }
  }

  render = () => {
    return (<></>);
  };
};

export default StoreStream;
